import { Link } from "react-router-dom";
import { TableColumn } from "react-data-table-component";
import { ITeamMember, ProbationTeamMember } from "../../types";
import {
  Action,
  Attendance,
  Conveyance,
  Email,
  Name,
  primary,
  secondary,
  infoColor,
  employeeUpdateProbationModal,
} from "../../../../../utils/Constant";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { fetchSurveyFormResponse } from "../../../../surveyForms/FormResponse/redux/slice/SurveyFormResponseSlice";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { AppDispatch } from "../../../../../ReduxToolkit/Store";
import { useEffect, useMemo, useState } from "react";
import { useStaggeredRowStyles } from "../../../../../hooks/useStaggeredRowStyle";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";
import {
  setEmployeePrimaryTabActive,
  setEmployeeSecondaryTabActive,
} from "../../../../employeeManagement/redux/slice/EmployeeManagementSlice";

export const badgeLinks = [
  {
    tab: 7,
    color: secondary,
    text: Attendance,
    primaryTab: 4,
    secondaryTab: 401,
  },
  {
    tab: 8,
    color: infoColor,
    text: Conveyance,
    primaryTab: 3,
    secondaryTab: 301,
  },
  { tab: 6, color: primary, text: "Leaves", primaryTab: 4, secondaryTab: 402 },
];

interface CommonProps<T> {
  type: "teamMember" | "probationMember";
  data: T[];
  dispatch: AppDispatch;
  setSelectedUser?: (user: any) => void; // required for probation
  loading?: boolean;
}

const TeamMembersTable = ({
  type,
  data,
  dispatch,
  setSelectedUser,
  loading,
}: CommonProps<ITeamMember | ProbationTeamMember>) => {
  const [animate, setAnimate] = useState(false);

  const handleOnClick = (primaryTab: number, secondaryTab: number) => {
    dispatch(
      setEmployeePrimaryTabActive({ tabId: primaryTab, employee: null })
    );
    dispatch(setEmployeeSecondaryTabActive(secondaryTab));
  };

  // Base columns
  const baseColumns: TableColumn<any>[] = [
    {
      name: "#",
      cell: (_row, index: number) => index + 1,
      width: "60px",
    },
    {
      name: Name,
      selector: (row) => row.name,
      sortable: true,
      width: "200px",
    },
    {
      name: Email,
      selector: (row) => row.email,
      sortable: true,
      width: "300px",
    },
  ];

  // Probation-only columns
  const probationColumns: TableColumn<ProbationTeamMember>[] =
    type === "probationMember"
      ? [
          {
            name: "Date of Joining",
            selector: (row) => row.doj ?? "-",
            sortable: true,
            width: "200px",
          },
          {
            name: "Probation Date",
            selector: (row) => row.probation_date ?? "-",
            sortable: true,
            width: "200px",
          },
          {
            name: "Extended Date",
            selector: (row) => row.extended_date ?? "NA",
            sortable: true,
            width: "200px",
          },
        ]
      : [];

  // Action column
  const actionColumn: TableColumn<any> = {
    name: Action,
    cell: (row: any) => (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ gap: "10px" }}
      >
        {badgeLinks.map(({ color, text, primaryTab, secondaryTab }) => (
          <Link
            key={`${primaryTab}-${secondaryTab}`}
            to={`/user-management/user/${row.id}`}
            onClick={() => handleOnClick(primaryTab, secondaryTab)}
          >
            <BadgeDetails
              color={color}
              text={text}
              className="rounded-pill pointer py-2 px-4 f-13"
            />
          </Link>
        ))}

        {type === "probationMember" && (
          <IconComponent
            className="fa fa-edit f-20 text-primary pointer ms-3"
            onClick={() => {
              const selected = { ...row, user_id: row.id };

              if (row.form_id && row.id) {
                dispatch(
                  fetchSurveyFormResponse({
                    formId: Number(row.form_id),
                    userId: Number(row.id),
                  })
                );
              }

              setSelectedUser?.(selected);
              dispatch(openModal({ modalType: employeeUpdateProbationModal }));
            }}
          />
        )}
      </div>
    ),
    width: type === "probationMember" ? "400px" : "350px",
    center: true,
  };

  const columns: TableColumn<any>[] = [
    ...baseColumns,
    ...probationColumns,
    actionColumn,
  ];

  //  Add row class with animation delay
  const tableData = useMemo(() => {
    return (data ?? []).map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [data]);

  useEffect(() => {
    if (!loading && data?.length > 0) {
      const timeout = setTimeout(() => setAnimate(true), 50);
      return () => clearTimeout(timeout);
    }
    setAnimate(false);
  }, [loading, data]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar mt-4 d-none d-lg-block">
      <CustomDataTable
        striped
        noHeader
        columns={columns}
        data={tableData}
        loading={loading}
        loaderComponent={
          <SkeletonTable
            rows={data.length || 8}
            columns={columns}
            rowHeight={40}
            gap={4}
          />
        }
      />
    </div>
  );
};

export default TeamMembersTable;
